import {
  Flex,
  Tag,
  TagLabel,
  TagLeftIcon,
  Text,
  TextProps,
  Tooltip,
} from "@chakra-ui/react";
import { RiStarLine } from "react-icons/ri";
import {
  StatusTag,
  StatusTagMetadata,
} from "src/components/DataDisplay/StatusTag";
import { BoundaryTag } from "src/components/Tags/BoundaryTag";
import { WEGLOT_SKIP_CLASS } from "src/plugins/weglot/constants";
import { SchoolWithBoundaryTags } from ".";

// Eligibility label shown only during staff school selection under the override
// feature. `labels` carries the (optionally glossarized) label text.
export type EligibilityLabel = {
  isEligible: boolean;
  eligibleLabel: string;
  ineligibleLabel: string;
};

type Props = {
  school: SchoolWithBoundaryTags;
  showAddress?: boolean;
  textProps?: TextProps;
  subStatus?: StatusTagMetadata;
  offerExpiresAt?: string;
  direction?: "column" | "row";
  eligibilityLabel?: EligibilityLabel;
};
const MakeStatusTag = (
  subStatus: StatusTagMetadata,
  offerExpiresAt?: string
) => {
  if (!offerExpiresAt) return <StatusTag status={subStatus} />;
  const tooltipText = `Offer will expire on ${offerExpiresAt}`;
  return (
    <Tooltip label={tooltipText} placement="top" hasArrow textAlign="center">
      <span>
        <StatusTag status={subStatus} />
      </span>
    </Tooltip>
  );
};

export const SchoolListItem: React.FC<Props> = ({
  school,
  showAddress,
  textProps,
  subStatus,
  offerExpiresAt,
  direction = "column",
  eligibilityLabel,
}) => {
  const ineligible =
    eligibilityLabel !== undefined && !eligibilityLabel.isEligible;
  return (
    <Flex
      direction={direction}
      className={WEGLOT_SKIP_CLASS}
      gap={2}
      color={ineligible ? "gray.400" : undefined}
    >
      <Flex direction="column">
        <Flex align="center" gap={2}>
          <Text {...textProps}>{school.name}</Text>

          {eligibilityLabel &&
            (eligibilityLabel.isEligible ? (
              <Tooltip
                label={eligibilityLabel.eligibleLabel}
                placement="right"
                hasArrow
              >
                <Tag
                  size="sm"
                  borderRadius="md"
                  bg="white"
                  color="green.600"
                  border="1px solid"
                  borderColor="green.500"
                >
                  <TagLeftIcon as={RiStarLine} color="green.600" />
                  <TagLabel>{eligibilityLabel.eligibleLabel}</TagLabel>
                </Tag>
              </Tooltip>
            ) : (
              <Tooltip
                label={eligibilityLabel.ineligibleLabel}
                placement="right"
                hasArrow
              >
                <Tag
                  size="sm"
                  borderRadius="md"
                  bg="white"
                  color="gray.800"
                  border="1px solid"
                  borderColor="gray.300"
                >
                  <TagLabel>{eligibilityLabel.ineligibleLabel}</TagLabel>
                </Tag>
              </Tooltip>
            ))}
        </Flex>
        {showAddress && <Text>{school.street_address}</Text>}
      </Flex>
      {subStatus && MakeStatusTag(subStatus, offerExpiresAt)}
      {school.boundaryTags !== undefined && school.boundaryTags.length > 0 ? (
        <Flex direction="row" gap="2">
          {school.boundaryTags?.map((tag) => (
            <BoundaryTag tag={tag} key={tag} size="sm" />
          ))}
        </Flex>
      ) : null}
    </Flex>
  );
};
